import React, { useState, useEffect } from 'react';
import { Container, Card, Button } from 'react-bootstrap';
import { useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  ShieldCheck,
  Shield,
  ChevronRight,
  AlertCircle,
  CheckCircle,
} from 'lucide-react';
import { toast } from 'react-toastify';
import authService from '../services/authService';

const VerificationPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [kycStatus, setKycStatus] = useState({
    preliminaryStatus: null,
    advancedStatus: null,
  });

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const response = await authService.getKYCStatus();

        // Backend returns single status for Advanced KYC
        const advancedStatus = response?.status || null;

        // Get preliminary status from user profile
        const user = authService.getCurrentUser();
        const hasPreliminaryData =
          user?.phone && user?.fullName && user?.age && user?.address;
        const preliminaryStatus = hasPreliminaryData
          ? user?.kycStatus || 'PENDING'
          : null;

        setKycStatus({
          preliminaryStatus: preliminaryStatus,
          advancedStatus: advancedStatus,
        });
      } catch (error) {
        // Don't show error toast if API endpoint doesn't exist yet
        if (error.message !== 'Failed to fetch KYC status') {
          toast.error(error.message, {
            position: 'bottom-left',
            autoClose: 3000,
          });
        }
      } finally {
        setLoading(false);
      }
    };
    fetchStatus();
  }, []);

  return (
    <div
      style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        minHeight: '100vh',
      }}
    >
      <Container fluid className='p-0'>
        {/* Header */}
        <div className='d-flex justify-content-between align-items-center p-3 text-white'>
          <Button
            variant='link'
            onClick={() => navigate(-1)}
            className='text-white p-0'
          >
            <ArrowLeft size={24} />
          </Button>
          <h5 className='mb-0 fw-bold'>Verification</h5>
          <div style={{ width: '24px' }}></div>
        </div>

        {/* Info Section */}
        <div className='text-center text-white px-3 pb-4'>
          <div
            className='rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3'
            style={{
              width: '80px',
              height: '80px',
              backgroundColor: 'rgba(255,255,255,0.2)',
            }}
          >
            <ShieldCheck size={40} color='white' />
          </div>
          <h6 className='fw-bold mb-2'>Complete Your Verification</h6>
          <p className='small opacity-75 mb-0' style={{ fontSize: '0.85rem' }}>
            Verify your identity to unlock all features
          </p>
        </div>

        {/* Main Content */}
        <div
          className='bg-white'
          style={{
            borderTopLeftRadius: '25px',
            borderTopRightRadius: '25px',
            minHeight: 'calc(100vh - 250px)',
          }}
        >
          <div className='p-3 p-sm-4'>
            {/* Preliminary Verification */}
            <Link
              to='/preliminary-verification'
              className='text-decoration-none'
            >
              <Card className='mb-3 border-0 shadow-sm hover-lift'>
                <Card.Body className='p-3'>
                  <div
                    className='d-flex align-items-center'
                    style={{ gap: '0.75rem' }}
                  >
                    <div
                      className='rounded-circle d-flex align-items-center justify-content-center flex-shrink-0'
                      style={{
                        width: '50px',
                        height: '50px',
                        backgroundColor: '#FFA726',
                      }}
                    >
                      <Shield size={22} color='white' />
                    </div>
                    <div className='flex-grow-1' style={{ minWidth: 0 }}>
                      <div
                        className='fw-semibold text-dark'
                        style={{ fontSize: '0.95rem', lineHeight: '1.3' }}
                      >
                        Preliminary Verification
                      </div>
                      <small
                        className='text-muted d-block text-truncate'
                        style={{ fontSize: '0.8rem' }}
                      >
                        Basic identity information
                      </small>
                    </div>
                    <div
                      className='d-flex align-items-center flex-shrink-0 ms-2'
                      style={{ gap: '0.5rem' }}
                    >
                      {!loading &&
                      kycStatus.preliminaryStatus === 'APPROVED' ? (
                        <span
                          className='badge px-2 py-1 d-flex align-items-center'
                          style={{
                            backgroundColor: '#E8F5E9',
                            color: '#2E7D32',
                            fontSize: '0.7rem',
                            gap: '0.25rem',
                          }}
                        >
                          <CheckCircle size={12} />
                          Verified
                        </span>
                      ) : !loading && kycStatus.preliminaryStatus ? (
                        <span
                          className='badge px-2 py-1 d-flex align-items-center'
                          style={{
                            // backgroundColor: '#FFF3E0',
                            // color: '#E65100',
                            fontSize: '0.7rem',
                            gap: '0.25rem',
                          }}
                        >
                          {/* <AlertCircle size={12} /> */}
                          {/* Pending */}
                        </span>
                      ) : !loading ? (
                        <span
                          className='badge px-2 py-1 d-flex align-items-center'
                          style={
                            {
                              // backgroundColor: '#FFEBEE',
                              // color: '#C62828',
                              // fontSize: '0.7rem',
                              // gap: '0.25rem',
                            }
                          }
                        >
                          <AlertCircle size={12} />
                          {/* Unverified */}
                        </span>
                      ) : null}
                      <ChevronRight size={18} color='#6c757d' />
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Link>

            {/* Advanced Verification */}
            <Link to='/advanced-verification' className='text-decoration-none'>
              <Card className='border-0 shadow-sm hover-lift'>
                <Card.Body className='p-3'>
                  <div
                    className='d-flex align-items-center'
                    style={{ gap: '0.75rem' }}
                  >
                    <div
                      className='rounded-circle d-flex align-items-center justify-content-center flex-shrink-0'
                      style={{
                        width: '50px',
                        height: '50px',
                        backgroundColor: '#42A5F5',
                      }}
                    >
                      <ShieldCheck size={22} color='white' />
                    </div>
                    <div className='flex-grow-1' style={{ minWidth: 0 }}>
                      <div
                        className='fw-semibold text-dark'
                        style={{ fontSize: '0.95rem', lineHeight: '1.3' }}
                      >
                        Advanced Verification
                      </div>
                      <small
                        className='text-muted d-block text-truncate'
                        style={{ fontSize: '0.8rem' }}
                      >
                        Upload ID and selfie verification
                      </small>
                    </div>
                    <div
                      className='d-flex align-items-center flex-shrink-0 ms-2'
                      style={{ gap: '0.5rem' }}
                    >
                      {!loading && kycStatus.advancedStatus === 'APPROVED' ? (
                        <span
                          className='badge px-2 py-1 d-flex align-items-center'
                          style={{
                            backgroundColor: '#E8F5E9',
                            color: '#2E7D32',
                            fontSize: '0.7rem',
                            gap: '0.25rem',
                          }}
                        >
                          <CheckCircle size={12} />
                          Verified
                        </span>
                      ) : !loading && kycStatus.advancedStatus === 'PENDING' ? (
                        <span
                          className='badge px-2 py-1 d-flex align-items-center'
                          style={{
                            backgroundColor: '#FFF3E0',
                            color: '#E65100',
                            fontSize: '0.7rem',
                            gap: '0.25rem',
                          }}
                        >
                          <AlertCircle size={12} />
                          Pending
                        </span>
                      ) : !loading && kycStatus.advancedStatus === 'REJECTED' ? (
                        <span
                          className='badge px-2 py-1 d-flex align-items-center'
                          style={{ backgroundColor: '#FFEBEE', color: '#C62828', fontSize: '0.7rem', gap: '0.25rem' }}
                        >
                          <AlertCircle size={12} />
                          Rejected
                        </span>
                      ) : !loading ? (
                        <span
                          className='badge px-2 py-1 d-flex align-items-center'
                          style={{
                            backgroundColor: '#E3F2FD',
                            color: '#1565C0',
                            fontSize: '0.7rem',
                            gap: '0.25rem',
                          }}
                        >
                          <AlertCircle size={12} />
                          Complete Verification
                        </span>
                      ) : null}
                      <ChevronRight size={18} color='#6c757d' />
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Link>
          </div>
        </div>
      </Container>

      <style jsx>{`
        .hover-lift {
          transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
        }
        .hover-lift:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
        }

        @media (max-width: 576px) {
          h5 {
            font-size: 1.1rem !important;
          }
          h6 {
            font-size: 1rem !important;
          }
          .card-body {
            padding: 0.75rem !important;
          }
        }

        @media (max-width: 380px) {
          h5 {
            font-size: 1rem !important;
          }
          h6 {
            font-size: 0.95rem !important;
          }
        }
      `}</style>
    </div>
  );
};

export default VerificationPage;
